import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import i18n from 'i18next'; // ✅ Required for syncing locale
import { useLoading } from "../../contexts/LoadingContext";

import EventStructuredData from "./components/EventStructuredData";
import SeoMeta from "../../components/SeoMeta";
import EventTitle from "./components/EventTitle";
import EventMap from "./components/EventMap";
import EventTime from "./components/EventTime";
import EventLinks from "./components/EventLinks";
import EventSponsor from "./components/EventSponsor";

// ✅ Get locale from cookie
const getLocale = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};

function Event() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const locale = getLocale();
  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;

  // ✅ Sync i18n with locale
  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale]);

  useEffect(() => {
    const fetchData = async () => {
      if (!STRAPI_URL || !STRAPI_TOKEN) return;

      const path = `/api/events?filters[slug][$eq]=${slug}&locale=${locale}&populate[image][populate]=*&populate[Address][populate]=*&populate[day][populate]=*&populate[links][populate]=*&populate[sponsors][populate]=*&populate[seo][populate]=*`;

      const url = new URL(STRAPI_URL + path);

      setIsLoading(true);
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${STRAPI_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          setPageData(result.data?.[0] || null);
        }
      } catch (err) {
        console.error("❌ Fetch error:", err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slug, locale, STRAPI_URL, STRAPI_TOKEN, setIsLoading]);

  if (!pageData) return null;

  return (
    <>
      <SeoMeta
        lang={locale}
        title={pageData?.seo?.Title || pageData?.title}
        description={pageData?.seo?.Description || pageData?.descritpion}
        keywords={pageData?.seo?.keywords}
        robots={pageData?.seo?.robots || "index, follow"}
        imageUrl={pageData?.seo?.image?.formats?.large?.url || pageData?.seo?.image?.url}
      />

      <EventStructuredData
        title={pageData?.title}
        description={pageData?.descritpion?.map(block => block.children.map(child => child.text).join(" ")).join("\n")}
        startDate={pageData?.startDate}
        endDate={pageData?.endDate}
        vernissage={pageData?.vernissageStartDateTime}
        entranceCost={pageData?.entranceCost}
        image={pageData?.image}
        placeName={pageData?.Address?.placeName}
        addressLine1={pageData?.Address?.addressLine1}
        addressLine2={pageData?.Address?.addressLine2}
        city={pageData?.Address?.city}
        zipCode={pageData?.Address?.zipCode}
        country={pageData?.Address?.country}
        latitude={pageData?.Address?.latitude}
        longitude={pageData?.Address?.longitude}
      />

      <section className="py-15 py-xl-20">
        <EventTitle
          title={pageData?.title}
          description={pageData?.descritpion}
          startDate={pageData?.startDate}
          endDate={pageData?.endDate}
          vernissage={pageData?.vernissageStartDateTime}
          entranceCost={pageData?.entranceCost}
          eventType={pageData?.eventType}
          placeName={pageData.Address?.placeName}
          city={pageData.Address?.city}
          country={pageData.Address?.country}
          image={Array.isArray(pageData?.image) ? pageData.image[0] : pageData?.image}
        />

        {pageData?.Address && (
          <EventMap
            placeName={pageData.Address?.placeName}
            addressLine1={pageData.Address?.addressLine1}
            addressLine2={pageData.Address?.addressLine2}
            city={pageData.Address?.city}
            zipCode={pageData.Address?.zipCode}
            country={pageData.Address?.country}
            latitude={pageData.Address?.latitude}
            longitude={pageData.Address?.longitude}
            url={pageData.Address?.url}
          />
        )}

        {pageData?.links && <EventLinks links={pageData.links} />}
        {pageData?.day && <EventTime days={pageData.day} />}
        {pageData?.sponsors && (
          <EventSponsor
            sponsors={pageData.sponsors}
            strapiBaseUrl={STRAPI_URL}
          />
        )}
      </section>
    </>
  );
}

export default Event;
